import React, {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useState
} from 'react';
import PropTypes from 'prop-types';
import {
    redirectToLogin,
    redirectToAccount,
    getTokenFromBrowser,
    getUserInfo
} from '../auth/sso';
import { metricsApi } from '../api/metricsApi';

const AuthContext = createContext();
const initialState = {
    loggedIn: false,
    user: {},
    productToken: null,
    loginPromptOpen: false,
    loginEnabled: true
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'login':
            return {
                ...state,
                loggedIn: true,
                user: action.user,
                productToken: action.productToken
            };

        case 'logout':
            return {
                ...state,
                loggedIn: false,
                user: {}
            };

        case 'loginPrompt':
            return {
                ...state,
                loginPromptOpen: action.value,
                loginPromptMessage: action.loginPromptMessage,
                loginPromptTitle: action.loginPromptTitle
            };

        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
};

export const AuthProvider = ({ children }) => {
    const [authStore, dispatch] = useReducer(reducer, initialState);
    const [attempts] = useState(0);

    async function metricCall(label) {
        await metricsApi.counterMetric(
            'sso_login_total',
            { status: label },
            'login counter'
        );
    }

    async function checkAuth() {
        // Skip auth check on callback page to prevent overlap
        if (window.location.pathname.includes('/sso/loginuser')) {
            return;
        }

        const productToken = getTokenFromBrowser();
        if (productToken) {
            const user = await getUserInfo(productToken);
            if (user) {
                dispatch({ type: 'login', user, productToken });
                metricCall('success');
            } else {
                metricCall('error');
            }
        }
    }

    useEffect(() => {
        checkAuth();
    }, [attempts]);

    return (
        <AuthContext.Provider value={{ authStore, dispatch }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const useLoginActions = () => {
    const { dispatch } = useAuth();

    const closeLoginPrompt = () => {
        dispatch({ type: 'loginPrompt', value: false });
    };

    const openLogin = () => {
        closeLoginPrompt();
        redirectToLogin();
    };

    const openMyAccount = () => {
        redirectToAccount();
    };

    const openRegistration = () => {
        closeLoginPrompt();
        redirectToLogin();
    };

    const openLoginPrompt = (message, title) => {
        dispatch({
            type: 'loginPrompt',
            value: true,
            loginPromptMessage: message,
            loginPromptTitle: title
        });
    };

    return {
        openLogin,
        openMyAccount,
        openRegistration,
        openLoginPrompt,
        closeLoginPrompt
    };
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
};
